import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Clock, ArrowRight, Check, Phone, Copy, CheckCheck } from 'lucide-react';
import '../pages/Blog.css';
import './Contact.css';

const CONTACT_EMAIL = 'hq@pidesk.in';
const OFFICE = 'Patna, Bihar, India';
const PHONES = ['+91 96932 33843', '+91 88874 10159'];

const include = [
  'What you want to build and who it is for',
  'Any deadline or launch date you have in mind',
  'A rough budget range, if you have one',
  'Links to references or existing work'
];

const faqs = [
  { q: 'How soon will I hear back?', a: 'We aim to reply within one business day with questions or next steps, and usually a full plan and estimate within two to three days of a short discovery call.' },
  { q: 'Do you work with colleges and small institutes, not just companies?', a: 'Yes. Most of what we build, including our College ERP and Learning App, is for colleges, schools and coaching institutes alongside businesses.' },
  { q: 'Do I need a full spec before reaching out?', a: 'No. A rough idea of what you want to build and who it is for is enough to start a conversation. We help turn that into a proper plan together.' },
  { q: 'Can you sign an NDA before discussing details?', a: "Yes, happy to sign an NDA before any detailed discussion if your project needs it." }
];

const Contact = () => {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard can be blocked (permissions, insecure context); the
      // email is still right there to select manually, so just no-op.
    }
  };

  return (
    <>
      <title>Contact - Pidesk</title>
      <meta name="description" content="Get in touch with Pidesk. Tell us about your project and we'll reply with a plan, timeline and estimate." />

      <div className="contact-page">
        <div className="container">
          <div className="contact-hero">
            <span className="contact-eyebrow">Contact</span>
            <h1>Let's talk about your project</h1>
            <p>Send us a short brief and we'll come back with a plan, timeline and estimate.</p>
          </div>

          <div className="contact-layout">
            <aside className="contact-side">
              <div className="contact-info-card">
                <div className="contact-info-row">
                  <div className="contact-info-icon"><Mail size={18} /></div>
                  <div className="contact-info-text">
                    <span>Email us</span>
                    <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                  </div>
                  <button type="button" className="copy-btn" onClick={copyEmail} aria-label="Copy email address">
                    {copied ? <CheckCheck size={16} /> : <Copy size={16} />}
                  </button>
                </div>

                <div className="contact-info-row">
                  <div className="contact-info-icon"><Phone size={18} /></div>
                  <div className="contact-info-text">
                    <span>Call / WhatsApp</span>
                    {PHONES.map((p) => <a key={p} href={`tel:${p.replace(/\s/g, '')}`}>{p}</a>)}
                  </div>
                </div>

                <div className="contact-info-row">
                  <div className="contact-info-icon"><MapPin size={18} /></div>
                  <div className="contact-info-text">
                    <span>Office</span>
                    <strong>{OFFICE}</strong>
                  </div>
                </div>
              </div>

              <div className="response-strip">
                <Clock size={16} />
                <span>We reply within <strong>one business day</strong></span>
              </div>
            </aside>

            <div className="contact-main">
              <div className="contact-panel">
                <h2>What to include in your email</h2>
                <ul className="contact-list">
                  {include.map((i) => <li key={i}><Check size={16} /> {i}</li>)}
                </ul>
                <div className="contact-panel-actions">
                  <a
                    className="btn btn-primary btn-lg"
                    href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('New project enquiry')}`}
                  >
                    Email your brief <ArrowRight size={18} style={{ marginLeft: 8 }} />
                  </a>
                  <Link className="btn btn-secondary btn-lg" to="/products">See our work</Link>
                </div>
              </div>

              <section className="contact-faq">
                <h2>Common questions</h2>
                {faqs.map((f) => (
                  <details key={f.q} className="faq-item">
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
